import React from "react";
import { fmtDay, rupees } from "../rates/rateModel";
import { useKT } from "../i18n/kt";
import type { AttendanceRow } from "./attendanceRows";
import { SELF_NOTE } from "./attendanceRows";
import { hours, reasonLabel } from "./punchModel";

type Props = {
  rows: AttendanceRow[];
  isToday: boolean;
  busyId: string | null;
  queued: Set<string>;
  newId?: string | null;
  onOpen: (r: AttendanceRow) => void;
  onIn: (r: AttendanceRow) => void;
  onOut: (r: AttendanceRow) => void;
};

/** What a row says under the name: since when, on which work, or that they aren't in yet. */
export const rowLine = (r: AttendanceRow, t: (k: string, v?: any) => string): string => {
  const work = `${t(r.work)}${r.loomCode ? ` · ${t("Loom")} ${r.loomCode}` : ""}`;
  if (r.kind === "IN") return t("In {{time}} · {{work}}", { time: r.since || r.firstIn || "", work });
  if (r.kind === "OUT") return `${t("Out {{time}}", { time: r.lastOut || "" })}${r.outReason ? ` · ${t(reasonLabel(r.outReason))}` : ""}`;
  if (r.kind === "FIX") return t("Missing an In or Out");
  return r.lastDay ? t("Not in yet · last came {{day}}", { day: fmtDay(r.lastDay) }) : t("Not in yet");
};

/** Phone and tablet: one person per row, one tap for In or Out, the name opens their day. */
const AttendanceList = ({ rows, isToday, busyId, queued, newId, onOpen, onIn, onOut }: Props) => {
  const { t } = useKT();
  return (
    <ul className="kat-list">
      {rows.map((r) => {
        const isNew = r.personId === newId;
        return (
          <li key={r.personId} className={`kat-row is-${r.kind}${isNew ? " is-new" : ""}`}>
            <button type="button" className="kat-row__main" onClick={() => onOpen(r)}>
              <strong>
                <span className="kw-code">{r.code}</span> {r.name}
                {isNew ? <span className="kat-new">{t("NEW")}</span> : null}
              </strong>
              <span>
                {rowLine(r, t)}
                {r.noRate ? <em className="kat-norate"> · {t("no day rate yet")}</em> : null}
                {r.kind !== "NOT_YET" && r.minutes ? ` · ${hours(r.minutes)}${r.pay && Number(r.pay) > 0 ? ` · ${rupees(r.pay, 0)}` : ""}` : ""}
              </span>
              {r.self ? <em className="kat-self">{t(SELF_NOTE)}</em> : null}
            </button>
            <div className="kat-row__end">
              {r.self ? (
                <span className="kw-chip">{t("You")}</span>
              ) : queued.has(r.personId) ? (
                <span className="kw-chip is-info">{t("On phone")}</span>
              ) : r.waiting ? (
                <span className="kw-chip is-review">{t("Waiting")}</span>
              ) : !isToday || r.kind === "FIX" ? (
                <button type="button" className="kd-btn kd-btn--line kd-btn--sm" onClick={() => onOpen(r)}>
                  {r.kind === "FIX" ? t("Fix") : t("Open")}
                </button>
              ) : r.kind === "IN" ? (
                <button type="button" className="kd-btn kd-btn--line kd-btn--sm kat-act" disabled={busyId === r.personId} onClick={() => onOut(r)}>
                  {t("Out")}
                </button>
              ) : (
                <button type="button" className="kd-btn kd-btn--sm kat-act" disabled={busyId === r.personId} onClick={() => onIn(r)}>
                  {t("In")}
                </button>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
};

export default AttendanceList;
